// Platform detection utilities for cross-platform functionality
export interface PlatformInfo {
  isMobile: boolean;
  isDesktop: boolean;
  isTouchDevice: boolean;
  supportsHover: boolean;
  screenSize: 'small' | 'medium' | 'large';
}

export const detectPlatform = (): PlatformInfo => {
  const isTouchDevice = window.ontouchstart !== undefined || navigator.maxTouchPoints > 0;
  const screenWidth = window.innerWidth;
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Detect mobile devices
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent) || 
                   screenWidth < 768 || 
                   isTouchDevice;

  const isDesktop = !isMobile;
  const supportsHover = !isTouchDevice && isDesktop;

  let screenSize: 'small' | 'medium' | 'large' = 'small';
  if (screenWidth >= 1024) {
    screenSize = 'large';
  } else if (screenWidth >= 768) {
    screenSize = 'medium';
  }

  return {
    isMobile,
    isDesktop,
    isTouchDevice,
    supportsHover,
    screenSize
  };
};

export const getOptimalTouchTarget = (platform: PlatformInfo): number => {
  // Apple's Human Interface Guidelines recommend 44px minimum
  // Material Design recommends 48dp minimum
  return platform.isMobile ? 44 : 32;
};

export const getOptimalSpacing = (platform: PlatformInfo): {
  small: number;
  medium: number;
  large: number;
} => {
  const multiplier = platform.isMobile ? 1.5 : 1;
  return {
    small: 8 * multiplier,
    medium: 16 * multiplier,
    large: 24 * multiplier
  };
};

export const shouldUseHover = (platform: PlatformInfo): boolean => {
  return platform.supportsHover && platform.isDesktop;
};

export const getInteractionClasses = (platform: PlatformInfo): string => {
  const baseClasses = 'transition-colors duration-200';
  const hoverClasses = platform.supportsHover ? 'hover:bg-opacity-80' : '';
  const touchClasses = platform.isMobile ? 'active:scale-95 active:bg-opacity-90' : '';
  
  return `${baseClasses} ${hoverClasses} ${touchClasses}`.trim();
};

// Individual utility functions for testing
export const isMobile = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (!navigator.userAgent) return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  const screenWidth = window.innerWidth;
  const isTouchDevice = window.ontouchstart !== undefined || navigator.maxTouchPoints > 0;
  
  return /android.*mobile|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent) || 
         (screenWidth < 768 && isTouchDevice);
};

export const isTablet = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (!navigator.userAgent) return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  
  return /ipad|android(?!.*mobile)|tablet/i.test(userAgent);
};

export const isDesktop = (): boolean => {
  return !isMobile() && !isTablet();
};

export const isMac = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (!navigator.userAgent) return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  return /mac|iphone|ipad|ipod/i.test(userAgent);
};

export const isWindows = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (!navigator.userAgent) return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  return /win/i.test(userAgent);
};

export const isLinux = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (!navigator.userAgent) return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  return /linux|android/i.test(userAgent);
};

export const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return window.ontouchstart !== undefined || 
         navigator.maxTouchPoints > 0 || 
         (navigator as any).msMaxTouchPoints > 0;
};

export const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  if (isMobile()) return 'mobile';
  if (isTablet()) return 'tablet';
  return 'desktop';
};

export const getOperatingSystem = (): string => {
  if (typeof navigator === 'undefined') return 'Unknown';
  if (!navigator.userAgent) return 'Unknown';
  
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (/iphone|ipad|ipod/i.test(userAgent)) return 'iOS';
  if (/android/i.test(userAgent)) return 'Android';
  if (/mac/i.test(userAgent)) return 'macOS';
  if (/win/i.test(userAgent)) return 'Windows';
  if (/linux/i.test(userAgent)) return 'Linux';
  
  return 'Unknown';
};